import { gql, useMutation } from '@apollo/client'
import imageCompression from 'browser-image-compression'
import * as chrono from 'chrono-node'
import AntigenTestGuide from 'components/AntigenTestGuide'
import KitIdForm, { KitRegistrationInfo } from 'components/Registration/KitIdForm'
import { setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns/fp'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import rollbar from 'helpers/rollbar'
import { toFriendlyKitId } from 'helpers/string'
import { flow, isEmpty } from 'lodash/fp'
import { ReactNode, useEffect, useState } from 'react'
import { FaUndo } from 'react-icons/fa'
import { PhotoDataInput, TestPurpose } from '../../__generated__/globalTypes'
import CheckEmailNotice from './Registration/CheckEmailNotice'
import DetailsForm, { getInitialValues } from './Registration/DetailsForm'
import ReviewDetails from './Registration/ReviewDetails'
import SubmitButton from './SubmitButton'
import { RegisterKit, RegisterKitVariables } from './__generated__/RegisterKit'

const REGISTER_KIT_MUTATION = gql`
  mutation RegisterKit($input: RegisterKitInput!) {
    registerKit(input: $input) {
      success
      message
      emailVerificationError
      kitId
    }
  }
`

const getFileContents = async (file: File): Promise<PhotoDataInput> => {
  const resizedBlob = await imageCompression(file, {
    maxSizeMB: 0.25,
    //     onProgress: (progress) => console.log('RESIZE PROGRESS: ', progress),
    fileType: 'image/jpeg',
    maxIteration: 100,
  })

  console.log('Resize complete')

  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onloadend = () =>
      resolve({
        filename: file.name,
        base64: reader.result.toString(),
        mimeType: resizedBlob.type,
      })

    reader.readAsDataURL(resizedBlob)
  })
}

interface InstructionsProps {
  kitId: string
  sku: 'rapid_travel' | 'rapid_travel_hughes'
  observed?: boolean
  onComplete: () => void
}

const Instructions = ({ kitId, sku, observed, onComplete }: InstructionsProps) => {
  const KitId = () => <span className="is-family-monospace has-text-primary">{kitId}</span>

  const checks = [
    <>The details on my ID document are legible.</>,
    <>
      I took this picture between 15 and {sku === 'rapid_travel_hughes' ? '30' : '20'} minutes after adding my sample to
      the test.
    </>,
    <>
      I have written my kit ID (<KitId />) on the test cassette.
    </>,
    <>I have saved the picture to my device.</>,
  ]

  const [checksCompleted, setChecksCompleted] = useState(Object.fromEntries(checks.map((check, i) => [i, false])))

  const allChecksCompleted = Object.values(checksCompleted).every((val) => val === true)

  return (
    <>
      <div>
        <AntigenTestGuide kitId={toFriendlyKitId(kitId)} sku={sku} supervised={observed} />
      </div>
      <div className="mt-5">
        <h2 className="title is-4 has-text-midnightBlue">Checklist</h2>

        <ul className="mt-3">
          {checks.map((check, i) => (
            <li key={i}>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={checksCompleted[i]}
                  onChange={(e) =>
                    setChecksCompleted((prev) => ({
                      ...prev,
                      [i]: e.target.checked,
                    }))
                  }
                />{' '}
                {check}
              </label>
            </li>
          ))}
        </ul>
      </div>
      {!allChecksCompleted && (
        <div className="buttons is-right">
          <p className="help is-danger">Please review and check all the checklist items before proceeding</p>
        </div>
      )}
      <div className="buttons is-right mt-3">
        <SubmitButton className="is-midnightBlue" onClick={onComplete} disabled={!allChecksCompleted}>
          Next step: Submit results
        </SubmitButton>
      </div>
    </>
  )
}

const casualGbChrono = new chrono.Chrono(chrono.en.createCasualConfiguration(true))

const getSampleDateTime = (values: { sampleDate: string; sampleTimeHours: number; sampleTimeMinutes: number }): Date =>
  flow([
    (s: string) => casualGbChrono.parseDate(s),
    setHours(values.sampleTimeHours),
    setMinutes(values.sampleTimeMinutes),
    setSeconds(0),
    setMilliseconds(0),
  ])(values.sampleDate)

const regDetailsValuesToMutationVars = (values: any, testPurpose: TestPurpose, thirdPartyId?: string) => {
  const sampleDateTime = getSampleDateTime(values).toISOString()

  const collectReleaseInfo = ['COV2', 'COV5', 'COV8', 'DAY2_LFD'].includes(testPurpose)

  return {
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    dob: chrono.en.GB.parseDate(values.dob).toISOString().substring(0, 10),
    sex: values.sex,
    nationality: values.nationality,
    thirdPartySharing: thirdPartyId && {
      associateId: thirdPartyId,
      consentGiven: values.sharingConsent === 'true',
      consentText: 'n/a',
    },
    passportNumber: values.passportNumber,
    trackingNumber: values.trackingNumber,
    sampleDate: sampleDateTime,
    photoFile: values.photoFile,
    testPurpose,
    antigenResult: values.result,
    ttrInfo:
      collectReleaseInfo || values.ttr.homeAddress.postcode
        ? {
            ...values.ttr,
            departureDate: isEmpty(values.ttr.departureDate)
              ? null
              : casualGbChrono.parseDate(values.ttr.departureDate).toISOString().substring(0, 10),
            arrivalDate: isEmpty(values.ttr.arrivalDate)
              ? null
              : casualGbChrono.parseDate(values.ttr.arrivalDate).toISOString().substring(0, 10),

            day2or8: values.ttr.day2or8?.replace(' ', '_').toUpperCase(),
          }
        : null,
  }
}

const Banner = ({ kitId, onReset }) => (
  <div className="is-flex is-align-items-center is-flex-wrap-wrap">
    <p className="mr-3">
      You are registering kit ID <span className="has-text-weight-bold">{toFriendlyKitId(kitId)}</span>.
    </p>

    <button type="button" className="button is-text is-size-7 pl-0" onClick={onReset}>
      <FaUndo className="mr-2" /> Register a different kit
    </button>
    <hr />
  </div>
)

interface RegistrationFormProps {
  thirdPartyAssociateId?: string
  consentText?: string
  children?: ReactNode
}

const RegistrationForm = ({ children, consentText }: RegistrationFormProps) => {
  const [step, setStep] = useState('KIT_ID')
  const [registrationInfo, setRegistrationInfo] = useState<KitRegistrationInfo>()
  const [regDetails, setRegDetails] = useState()
  // No longer used
  // TODO: Remove other references
  const testPurpose = null

  const [registerKit] = useMutation<RegisterKit, RegisterKitVariables>(REGISTER_KIT_MUTATION)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const showKitIdBanner = !!registrationInfo?.kitId

  return (
    <>
      <GatsbySeo title="Register Kit" />

      <section className="section">
        <div className="container is-max-desktop">
          {children}

          {showKitIdBanner && (
            <Banner
              kitId={registrationInfo.kitId}
              onReset={() => {
                setStep('KIT_ID')
                setRegistrationInfo(undefined)
                setRegDetails(undefined)
              }}
            />
          )}

          {step === 'KIT_ID' && (
            <KitIdForm
              onComplete={(registrationInfo) => {
                setRegistrationInfo(registrationInfo)
                setRegDetails(getInitialValues(registrationInfo.sku))

                if (['rapid_event', 'rapid_travel', 'rapid_travel_hughes'].includes(registrationInfo.sku)) {
                  setStep('INSTRUCTIONS')
                  return
                }

                setStep('DETAILS')
              }}
            />
          )}

          {step === 'INSTRUCTIONS' && (
            <Instructions
              kitId={registrationInfo.kitId}
              sku={registrationInfo.sku}
              observed={registrationInfo.observed}
              onComplete={() => {
                setStep('DETAILS')
              }}
            />
          )}

          {step === 'DETAILS' && (
            <DetailsForm
              consentText={consentText}
              thirdPartyAssociateId={registrationInfo.testPartner?.id}
              registrationInfo={registrationInfo}
              testPurpose={testPurpose}
              initialValues={regDetails}
              onComplete={async (mutationVariables) => {
                setRegDetails(mutationVariables)
                setStep('REVIEW')
              }}
            />
          )}

          {step === 'REVIEW' && (
            <ReviewDetails
              onGoBack={() => setStep('DETAILS')}
              kitId={registrationInfo.kitId}
              mutationVariables={regDetailsValuesToMutationVars(
                regDetails,
                testPurpose,
                registrationInfo.testPartner?.id
              )}
              onComplete={async () => {
                try {
                  const { photoFile, ...vars } = regDetailsValuesToMutationVars(
                    regDetails,
                    testPurpose,
                    registrationInfo.testPartner?.id
                  )

                  const { data } = await registerKit({
                    variables: {
                      input: {
                        kitId: registrationInfo.kitId,
                        ...vars,
                        regSessionId: registrationInfo.sessionId,
                        ...(photoFile
                          ? {
                              photo: await getFileContents(photoFile),
                            }
                          : {}),
                      },
                    },
                  })

                  if (!data?.registerKit?.success) {
                    throw new Error(data.registerKit.message)
                  }

                  setStep('CHECK_EMAIL')
                } catch (err) {
                  console.error('Registration error', err)
                  rollbar.error('Registration error', err, {
                    kitId: registrationInfo.kitId,
                    regSessionId: registrationInfo.sessionId,
                    errorMessage: JSON.stringify(err),
                  })

                  throw new Error('')
                }
              }}
            />
          )}

          {step === 'CHECK_EMAIL' && <CheckEmailNotice email={regDetails.email} />}
        </div>
      </section>
    </>
  )
}

export default RegistrationForm
